/**
 * Anode Catalog Modal Module
 * Handles anode selection with filtering by material, application, and size
 */

import { supabase } from '../supabase/client.js';
import { calculateAnodePrices, formatPrice, getPricingStrategy, calculateAnodePriceWithStrategy } from '../utils/pricing.js';

// State for current filters
let currentFilters = {
    category: 'anodes',  // Fixed to anodes for this picker
    material: 'all',
    application: 'all',
    sizing: 'all'
};

// Anode catalog data - loaded from Supabase
let anodeCatalog = [];
let searchTerm = '';
let showImages = true;  // Toggle for showing/hiding product images

/**
 * Load anode catalog from Supabase
 */
async function loadAnodeCatalog() {
    try {
        console.log('📦 Loading anode catalog from Supabase...');

        // Query Supabase directly for anode catalog
        const { data, error } = await supabase
            .from('anodes_catalog')
            .select('*')
            .eq('is_active', true)
            .order('name');

        if (error) {
            console.error('Supabase query error:', error);
            throw error;
        }

        if (data && data.length > 0) {
            // Fetch pricing strategy ONCE for all anodes (major performance boost!)
            console.log('⚡ Fetching pricing strategy once for all anodes...');
            const pricingStrategy = await getPricingStrategy();

            // Map Supabase data to our format (no async operations here = FAST!)
            anodeCatalog = data.map(anode => {
                // Detect metric vs standard from name and dimensions
                let sizing = 'standard';
                const searchText = `${anode.name || ''} ${anode.dimensions || ''}`.toLowerCase();

                // Check for metric indicators (mm, cm in product name or dimensions)
                if (searchText.match(/\d+mm/) || searchText.match(/\d+cm/) ||
                    searchText.includes('millimeter') || searchText.includes('centimeter')) {
                    sizing = 'metric';
                }

                // Calculate customer price using cached pricing strategy
                const supplierCost = anode.sale_price ? parseFloat(anode.sale_price) : parseFloat(anode.list_price);
                const category = anode.category?.toLowerCase();

                let customerPrice = supplierCost;
                let marginDollars = 0;
                let marginPercent = 0;

                try {
                    const pricing = calculateAnodePriceWithStrategy(supplierCost, pricingStrategy, { category });
                    customerPrice = pricing.customerPrice;
                    marginDollars = pricing.marginDollars;
                    marginPercent = pricing.marginPercent;
                } catch (error) {
                    console.warn(`Error calculating price for ${anode.name}:`, error);
                    // Fallback to supplier cost if pricing fails
                }

                return {
                    id: anode.id,
                    boatzincs_id: anode.boatzincs_id,
                    name: anode.name,
                    description: anode.description,
                    application: (anode.category || anode.subcategory || '').toLowerCase(),
                    subcategory: anode.subcategory,
                    category: anode.category,
                    material: anode.material?.toLowerCase(),
                    price: parseFloat(anode.list_price),
                    sale_price: anode.sale_price ? parseFloat(anode.sale_price) : null,
                    supplier_cost: supplierCost,  // What you pay
                    customer_price: customerPrice,  // What customer pays
                    margin_dollars: marginDollars,  // Profit per unit
                    margin_percent: marginPercent,  // Profit percentage
                    is_on_sale: anode.is_on_sale,
                    dimensions: anode.dimensions,
                    weight: anode.weight,
                    part_number: anode.part_number,
                    manufacturer: anode.manufacturer,
                    stock_status: anode.stock_status,
                    sku: anode.sku,
                    sizing: sizing,
                    image_url: anode.image_url  // Include product image
                };
            });

            // Count sizing types
            const metricCount = anodeCatalog.filter(a => a.sizing === 'metric').length;
            const standardCount = anodeCatalog.filter(a => a.sizing === 'standard').length;

            // Count by material
            const zincCount = anodeCatalog.filter(a => a.material === 'zinc').length;
            const aluminumCount = anodeCatalog.filter(a => a.material === 'aluminum').length;
            const magnesiumCount = anodeCatalog.filter(a => a.material === 'magnesium').length;

            // Get unique applications
            const uniqueApps = [...new Set(anodeCatalog.map(a => a.application).filter(Boolean))];

            console.log(`✅ Loaded ${anodeCatalog.length} anodes from catalog`);
            console.log(`   📏 Sizing - Standard: ${standardCount}, Metric: ${metricCount}`);
            console.log(`   🔩 Materials - Zinc: ${zincCount}, Aluminum: ${aluminumCount}, Magnesium: ${magnesiumCount}`);
            console.log(`   📍 Applications found:`, uniqueApps.slice(0, 10));
        } else {
            console.warn('No anodes found in catalog');
            anodeCatalog = [];
        }
    } catch (error) {
        console.error('Error loading anode catalog:', error);
        // Use empty catalog if load fails
        anodeCatalog = [];
    }
}

/**
 * Open the anode catalog modal
 * @param {string} location - Location on the boat (shaft, prop, etc.)
 * @param {string} position - Position (port, starboard, etc.)
 * @param {Function} onSelect - Callback function when anode is selected
 */
export async function openAnodeCatalogModal(location, position, onSelect) {
    // Remove existing modal if present
    closeAnodeCatalogModal();

    // Show loading modal first
    const loadingHtml = `
        <div class="modal-overlay" id="anodeCatalogModal">
            <div class="anode-modal-content">
                <div class="anode-modal-header">
                    <h3>Loading Anode Catalog...</h3>
                </div>
                <div class="anode-modal-body" style="text-align: center; padding: 40px;">
                    <div style="font-size: 18px; color: #666;">⏳ Loading products from inventory...</div>
                </div>
            </div>
        </div>
    `;
    document.body.insertAdjacentHTML('beforeend', loadingHtml);

    // Load catalog if not already loaded
    if (anodeCatalog.length === 0) {
        await loadAnodeCatalog();

        // Check if load failed
        if (anodeCatalog.length === 0) {
            document.getElementById('anodeCatalogModal')?.remove();
            alert('Failed to load anode catalog from database. Please check your connection and try again.');
            return;
        }
    }

    // Remove loading modal
    document.getElementById('anodeCatalogModal')?.remove();

    // Reset filters
    currentFilters = {
        category: 'anodes',
        material: 'all',
        application: 'all',
        sizing: 'all'
    };
    searchTerm = '';

    // Create modal HTML matching inventory app style
    const modalHtml = `
        <div class="modal-overlay" id="anodeCatalogModal" onclick="window.__closeAnodeCatalogModal(event)">
            <div class="anode-modal-content" onclick="event.stopPropagation()" style="max-width: 95%; width: 1400px;">
                <div class="anode-modal-header">
                    <h3>Select Anode for ${location.charAt(0).toUpperCase() + location.slice(1)} - ${position}</h3>
                    <button onclick="window.__closeAnodeCatalogModal()" class="anode-modal-close">&times;</button>
                </div>
                <div class="anode-modal-body">
                    <!-- Search Box -->
                    <div style="display: flex; gap: 10px; margin-bottom: 20px;">
                        <input type="text" id="anodeSearch" placeholder="Search anodes..."
                               style="flex: 1; padding: 10px; border: 1px solid #ddd; border-radius: 5px; font-size: 16px;"
                               oninput="window.__searchAnodes(this.value)">
                        <button class="anode-filter-btn" onclick="window.__refreshCatalog()"
                                style="padding: 10px 20px; background: #4a5568; color: white; border-radius: 5px;">
                            ↻ Refresh
                        </button>
                    </div>

                    <!-- Category Filters -->
                    <div class="anode-filter-group">
                        <label class="anode-filter-label">Category:</label>
                        <div class="anode-filter-buttons">
                            <button id="cat-all" class="anode-filter-btn" onclick="window.__setFilter('category', 'all')">All Items</button>
                            <button id="cat-anodes" class="anode-filter-btn selected" onclick="window.__setFilter('category', 'anodes')">⚓ Anodes</button>
                            <button id="cat-tools" class="anode-filter-btn" onclick="window.__setFilter('category', 'tools')">🔧 Tools</button>
                            <button id="cat-equipment" class="anode-filter-btn" onclick="window.__setFilter('category', 'equipment')">⚙️ Equipment</button>
                        </div>
                    </div>

                    <!-- Material Filters -->
                    <div class="anode-filter-group">
                        <label class="anode-filter-label">Material:</label>
                        <div class="anode-filter-buttons">
                            <button id="mat-all" class="anode-filter-btn selected" onclick="window.__setFilter('material', 'all')">All Materials</button>
                            <button id="mat-zinc" class="anode-filter-btn" onclick="window.__setFilter('material', 'zinc')">Zinc</button>
                            <button id="mat-aluminum" class="anode-filter-btn" onclick="window.__setFilter('material', 'aluminum')">Aluminum</button>
                            <button id="mat-magnesium" class="anode-filter-btn" onclick="window.__setFilter('material', 'magnesium')">Magnesium</button>
                        </div>
                    </div>

                    <!-- Application Filters -->
                    <div class="anode-filter-group">
                        <label class="anode-filter-label">Application:</label>
                        <div class="anode-filter-buttons" style="flex-wrap: wrap;">
                            <button id="app-all" class="anode-filter-btn selected" onclick="window.__setFilter('application', 'all')">All Applications</button>
                            <button id="app-shaft" class="anode-filter-btn" onclick="window.__setFilter('application', 'shaft')">Shaft</button>
                            <button id="app-hull" class="anode-filter-btn" onclick="window.__setFilter('application', 'hull')">Hull</button>
                            <button id="app-collar" class="anode-filter-btn" onclick="window.__setFilter('application', 'collar')">Collar</button>
                            <button id="app-engine" class="anode-filter-btn" onclick="window.__setFilter('application', 'engine')">Engine</button>
                            <button id="app-propeller" class="anode-filter-btn" onclick="window.__setFilter('application', 'propeller')">Propeller</button>
                            <button id="app-rudder" class="anode-filter-btn" onclick="window.__setFilter('application', 'rudder')">Rudder/Trim</button>
                        </div>
                    </div>

                    <!-- Sizing Filters -->
                    <div class="anode-filter-group">
                        <label class="anode-filter-label">Sizing:</label>
                        <div class="anode-filter-buttons">
                            <button id="size-all" class="anode-filter-btn selected" onclick="window.__setFilter('sizing', 'all')">All Sizes</button>
                            <button id="size-standard" class="anode-filter-btn" onclick="window.__setFilter('sizing', 'standard')">📏 Standard</button>
                            <button id="size-metric" class="anode-filter-btn" onclick="window.__setFilter('sizing', 'metric')">🔧 Metric</button>
                            <div class="filter-divider"></div>
                            <button id="toggle-images" class="anode-filter-btn ${showImages ? 'selected' : ''}" onclick="window.__toggleImages()">🖼️ Images</button>
                        </div>
                    </div>

                    <!-- Anode Grid -->
                    <div id="anodeModalGrid" class="anode-grid">
                        <!-- Grid items will be populated by renderAnodeGrid() -->
                    </div>
                </div>
            </div>
        </div>
    `;

    // Add modal to page
    document.body.insertAdjacentHTML('beforeend', modalHtml);

    // Store callback for later use
    window.__anodeSelectCallback = onSelect;

    // Render initial grid
    renderAnodeGrid();
}

/**
 * Close the anode catalog modal
 * @param {Event} event - Optional click event
 */
export function closeAnodeCatalogModal(event) {
    if (event && event.target !== event.currentTarget) return;
    const modal = document.getElementById('anodeCatalogModal');
    if (modal) modal.remove();
    delete window.__anodeSelectCallback;
}

/**
 * Set a filter value and update the display
 * @param {string} filterType - Type of filter (material, category, application, sizing)
 * @param {string} value - Filter value
 */
export function setFilter(filterType, value) {
    currentFilters[filterType] = value;

    // Update button states
    const prefixes = {
        'material': 'mat',
        'category': 'cat',
        'application': 'app',
        'sizing': 'size'
    };
    const filterGroup = prefixes[filterType];

    // Remove 'selected' class from all buttons in this group
    document.querySelectorAll(`[id^="${filterGroup}-"]`).forEach(btn => {
        btn.classList.remove('selected');
    });

    // Add 'selected' class to the clicked button
    const buttonId = `${filterGroup}-${value.toLowerCase().replace(/\s+/g, '-').replace(/\//g, '')}`;
    const selectedButton = document.getElementById(buttonId);
    if (selectedButton) {
        selectedButton.classList.add('selected');
    }

    // Re-render grid with new filters
    renderAnodeGrid();
}

/**
 * Search anodes by name or SKU
 * @param {string} term - Search term
 */
export function searchAnodes(term) {
    searchTerm = term.toLowerCase();
    renderAnodeGrid();
}

/**
 * Refresh the catalog by reloading from database
 */
export async function refreshCatalog() {
    anodeCatalog = [];
    await loadAnodeCatalog();
    renderAnodeGrid();
}

/**
 * Extract numeric size from anode name/dimensions for sorting
 * @param {object} anode - Anode object
 * @returns {number} - Numeric size value
 */
function extractSize(anode) {
    const searchText = `${anode.name || ''} ${anode.dimensions || ''}`;

    // Try fractional inches like 1-1/2", 2-1/2", 3-1/2" (check this FIRST before simple inch)
    const fracMatch = searchText.match(/(\d+)-(\d+)\/(\d+)/i);
    if (fracMatch) {
        const whole = parseFloat(fracMatch[1]);
        const num = parseFloat(fracMatch[2]);
        const denom = parseFloat(fracMatch[3]);
        return (whole + num/denom) * 25.4;
    }

    // Try simple fraction like 1/2", 3/4", 5/8"
    const simpleFracMatch = searchText.match(/(\d+)\/(\d+)"/i);
    if (simpleFracMatch) {
        const num = parseFloat(simpleFracMatch[1]);
        const denom = parseFloat(simpleFracMatch[2]);
        return (num/denom) * 25.4;
    }

    // Try simple inch measurement like 1", 2", 3" (check AFTER fractions)
    const inchMatch = searchText.match(/\s(\d+\.?\d*)"/i);
    if (inchMatch) {
        return parseFloat(inchMatch[1]) * 25.4;
    }

    // Try mm measurement
    const mmMatch = searchText.match(/(\d+\.?\d*)mm/i);
    if (mmMatch) {
        return parseFloat(mmMatch[1]);
    }

    // Try cm measurement (convert to mm for comparison)
    const cmMatch = searchText.match(/(\d+\.?\d*)cm/i);
    if (cmMatch) {
        return parseFloat(cmMatch[1]) * 10;
    }

    // Default to 999999 to sort unknown sizes to the end
    return 999999;
}

/**
 * Filter anodes based on current filters and search term
 * @returns {Array} Filtered array of anodes (sorted by size)
 */
function getFilteredAnodes() {
    const filtered = anodeCatalog.filter(anode => {
        // Apply search filter
        if (searchTerm) {
            const nameMatch = anode.name?.toLowerCase().includes(searchTerm);
            const skuMatch = anode.boatzincs_id?.toLowerCase().includes(searchTerm) ||
                           anode.sku?.toLowerCase().includes(searchTerm);
            if (!nameMatch && !skuMatch) return false;
        }

        // Category filter (for now, only show anodes)
        if (currentFilters.category !== 'all' && currentFilters.category !== 'anodes') {
            return false;
        }

        // Material filter
        if (currentFilters.material !== 'all' && anode.material !== currentFilters.material) {
            return false;
        }

        // Application filter - more flexible matching
        if (currentFilters.application !== 'all') {
            if (!anode.application) return false;

            const app = anode.application.toLowerCase();
            const filter = currentFilters.application.toLowerCase();

            // Special case: "propeller" should also match "prop"
            if (filter === 'propeller' && (app.includes('prop') || app.includes('propeller'))) {
                // Match found
            } else if (filter === 'rudder' && (app.includes('rudder') || app.includes('trim'))) {
                // Match rudder/trim
            } else if (!app.includes(filter)) {
                return false;
            }
        }

        // Sizing filter
        if (currentFilters.sizing !== 'all' && anode.sizing !== currentFilters.sizing) {
            return false;
        }

        return true;
    });

    // Sort filtered anodes by size (smallest to largest)
    const sorted = filtered.sort((a, b) => {
        const sizeA = extractSize(a);
        const sizeB = extractSize(b);
        return sizeA - sizeB;
    });

    return sorted;
}

/**
 * Toggle image display in the grid
 */
export function toggleImages() {
    showImages = !showImages;

    // Update button state
    const toggleBtn = document.getElementById('toggle-images');
    if (toggleBtn) {
        if (showImages) {
            toggleBtn.classList.add('selected');
        } else {
            toggleBtn.classList.remove('selected');
        }
    }

    // Update grid class
    const grid = document.getElementById('anodeModalGrid');
    if (grid) {
        if (showImages) {
            grid.classList.remove('hide-images');
        } else {
            grid.classList.add('hide-images');
        }
    }
}

/**
 * Render the anode grid based on current filters
 */
function renderAnodeGrid() {
    const grid = document.getElementById('anodeModalGrid');
    if (!grid) return;

    const filteredAnodes = getFilteredAnodes();

    console.log(`🔍 Filtering with:`, currentFilters);
    console.log(`   Found ${filteredAnodes.length} matching anodes`);

    if (filteredAnodes.length === 0) {
        grid.innerHTML = '<div class="anode-grid-empty">No anodes match the selected filters</div>';
        return;
    }

    grid.innerHTML = filteredAnodes.map(anode => `
        <div class="anode-grid-item" data-anode-id="${anode.id}" onclick="window.__selectAnode('${anode.id}')">
            ${anode.image_url ?
                `<img src="${anode.image_url}" alt="${anode.name}" class="anode-item-image">` :
                `<div class="anode-item-image anode-item-image-placeholder">
                    <span style="font-size: 48px; color: #ddd;">⚓</span>
                </div>`
            }
            <div class="anode-item-info">
                <div class="anode-item-name">${anode.name}</div>
                <div class="anode-item-price" style="font-weight: 600; color: #2c3e50; font-size: 16px;">
                    $${anode.customer_price.toFixed(2)}
                </div>
                <div style="font-size: 11px; color: #7f8c8d; margin-top: 2px;">
                    Cost: $${anode.supplier_cost.toFixed(2)} • Margin: ${anode.margin_percent.toFixed(0)}%
                </div>
                <div class="anode-item-details" style="font-size: 12px; color: #666;">
                    ${anode.material ? anode.material.charAt(0).toUpperCase() + anode.material.slice(1) : ''}
                    ${anode.dimensions ? ' • ' + anode.dimensions : ''}
                    ${anode.application ? ' • ' + anode.application : ''}
                </div>
                <div class="anode-inventory-badge" data-anode-id="${anode.id}" style="font-size: 11px; margin-top: 4px;">
                    <!-- Inventory badge will be populated dynamically -->
                </div>
            </div>
        </div>
    `).join('');

    // Apply hide-images class if needed
    if (!showImages) {
        grid.classList.add('hide-images');
    } else {
        grid.classList.remove('hide-images');
    }

    // Fetch and display real-time inventory badges
    fetchAndDisplayInventoryBadges(filteredAnodes);
}

/**
 * Fetch and display real-time inventory badges for displayed anodes
 * Shows "Out of Stock", "On packing list", or "(X available)" based on actual inventory
 * @param {Array} anodes - Array of anode objects currently displayed in the grid
 */
async function fetchAndDisplayInventoryBadges(anodes) {
    if (!anodes || anodes.length === 0) return;

    try {
        // Batch anodes to avoid URL length limits (max 50 per batch)
        const BATCH_SIZE = 50;
        const inventoryMap = new Map();

        // Process in batches
        for (let i = 0; i < anodes.length; i += BATCH_SIZE) {
            const batch = anodes.slice(i, i + BATCH_SIZE);
            const anodeIds = batch.map(a => a.id);

            // Fetch inventory data for this batch
            const { data: inventoryData, error } = await supabase
                .from('anode_inventory')
                .select('anode_id, quantity_on_hand, quantity_allocated, quantity_available')
                .in('anode_id', anodeIds);

            if (error) {
                console.error(`❌ Error fetching inventory data for batch ${Math.floor(i / BATCH_SIZE) + 1}:`, error);
                continue; // Skip this batch but continue with others
            }

            // Add to inventory map
            inventoryData?.forEach(inv => {
                inventoryMap.set(inv.anode_id, inv);
            });
        }

        // Update each anode card with inventory badge
        anodes.forEach(anode => {
            const badgeElement = document.querySelector(`.anode-inventory-badge[data-anode-id="${anode.id}"]`);
            if (!badgeElement) return;

            const inventory = inventoryMap.get(anode.id);

            if (!inventory) {
                // No inventory record - assume out of stock
                badgeElement.innerHTML = '<span style="color: #e74c3c; font-weight: 600;">Out of Stock</span>';
                return;
            }

            // Determine badge based on inventory levels
            if (inventory.quantity_on_hand === 0) {
                // No inventory at all
                badgeElement.innerHTML = '<span style="color: #e74c3c; font-weight: 600;">Out of Stock</span>';
            } else if (inventory.quantity_available > 0) {
                // Available inventory
                badgeElement.innerHTML = `<span style="color: #27ae60; font-weight: 600;">(${inventory.quantity_available} available)</span>`;
            } else {
                // On hand but all allocated (on packing list or reserved)
                badgeElement.innerHTML = `<span style="color: #f39c12; font-weight: 600;">On packing list (${inventory.quantity_on_hand} allocated)</span>`;
            }
        });

        console.log(`✅ Updated inventory badges for ${anodes.length} anodes (${Math.ceil(anodes.length / BATCH_SIZE)} batches)`);
    } catch (error) {
        console.error('❌ Error fetching/displaying inventory badges:', error);
    }
}

/**
 * Select an anode from the catalog
 * @param {string} anodeId - ID of the selected anode (UUID)
 */
export function selectAnode(anodeId) {
    const anode = anodeCatalog.find(a => a.id === anodeId);
    if (!anode) {
        console.error('Anode not found:', anodeId);
        return;
    }

    // Call the callback if it exists
    if (typeof window.__anodeSelectCallback === 'function') {
        window.__anodeSelectCallback({
            anode_id: anode.id,
            anode_name: anode.name,
            anode_price: anode.customer_price,  // Customer price with markup applied
            supplier_cost: anode.supplier_cost,  // What you pay (for reference/tracking)
            margin_dollars: anode.margin_dollars,  // Profit per unit
            margin_percent: anode.margin_percent,  // Profit percentage
            boatzincs_id: anode.boatzincs_id,
            sku: anode.sku,
            application: anode.application,
            subcategory: anode.subcategory,
            material: anode.material,
            dimensions: anode.dimensions
        });
    }

    // Close the modal
    closeAnodeCatalogModal();
}

// Expose functions to window for onclick handlers
// (These will be removed when we refactor to use proper event listeners)
window.__closeAnodeCatalogModal = closeAnodeCatalogModal;
window.__setFilter = setFilter;
window.__selectAnode = selectAnode;
window.__searchAnodes = searchAnodes;
window.__refreshCatalog = refreshCatalog;
window.__toggleImages = toggleImages;
